import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import MessengerPanel from '../../components/dashboard/MessengerPanel';
import NotificationsPanel from '../../components/dashboard/NotificationsPanel';
import { translatorApi, fileUrl } from '../../api/client';
import {
  User, Lock, Briefcase, Loader, Hourglass, Flame, HelpCircle, RefreshCw,
  ClipboardList, Languages, UserCheck, ArrowRight, MessageSquare, UserCog,
  FileText, CalendarDays, SlidersHorizontal, RotateCcw, ChevronLeft, History,
  Package, Eye, Download, Upload, Inbox, Headphones, Check, X,
} from 'lucide-react';
import StatusBadge from '../../components/dashboard/StatusBadge';
import DashModal from '../../components/dashboard/DashModal';

import TranslatorProfileView from '../../components/dashboard/TranslatorProfileView';
import Pagination, { usePagination } from '../../components/dashboard/Pagination';
import PasswordChangeForm from '../../components/dashboard/PasswordChangeForm';
import '../../components/dashboard/AssignTranslatorForm.css';
import '../../components/dashboard/DashForm.css';
import '../admin/AdminHome.css';
import '../admin/AdminOrders.css';

function MissionsTable({ rows }) {
  const { t } = useLanguage();
  const tr = t.dashboard.translator;
  if (!rows.length) return <p className="dash-empty">{t.dashboard.empty}</p>;
  return (
    <div className="dash-table-wrap">
      <table className="dash-table">
        <thead>
          <tr>
            <th>{t.dashboard.client.colId}</th>
            <th>{t.dashboard.client.colDocument}</th>
            <th>{t.dashboard.client.colLangs}</th>
            <th>{tr.colClient}</th>
            <th>{t.dashboard.client.colStatus}</th>
            <th>{tr.colDeadline}</th>
            <th>{t.dashboard.actions}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.id}>
              <td>{m.id}</td>
              <td>{m.document}</td>
              <td>{m.langs}</td>
              <td>{m.client}</td>
              <td><StatusBadge status={m.status} labels={t.dashboard.status} /></td>
              <td>{m.deadline || '—'}</td>
              <td><Link className="dash-link" to={`/pro/missions/${m.id}`}>{t.dashboard.view}</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const REFUSE_REASONS = ['motifUnavailable', 'motifDeadline', 'motifSkill', 'motifWorkload', 'motifOther'];

function RefuseMissionModal({ open, busy, onConfirm, onClose }) {
  const { t } = useLanguage();
  const tr = t.dashboard.translator;
  const [reason, setReason] = useState(REFUSE_REASONS[0]);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) { setReason(REFUSE_REASONS[0]); setNote(''); }
  }, [open]);

  return (
    <DashModal open={open} title={tr.refuseTitle} onClose={onClose}>
      <div className="dash-form" style={{ maxWidth: '100%' }}>
        <p style={{ marginTop: 0, color: 'var(--gray-600)' }}>{tr.refuseHint}</p>
        <div className="dash-field">
          <label>{tr.motifLabel}</label>
          <select value={reason} onChange={(e) => setReason(e.target.value)}>
            {REFUSE_REASONS.map((k) => <option key={k} value={k}>{tr[k]}</option>)}
          </select>
        </div>
        <div className="dash-field">
          <label>{tr.motifNote}</label>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <div className="dash-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => onConfirm(`${tr[reason]}${note.trim() ? ` — ${note.trim()}` : ''}`)}
          >
            {tr.confirmRefuse}
          </button>
          <button type="button" className="btn btn-outline" onClick={onClose}>
            {t.dashboard.cancel}
          </button>
        </div>
      </div>
    </DashModal>
  );
}

export function TranslatorHomePage() {
  const { t } = useLanguage();
  const tr = t.dashboard.translator;
  const d = t.dashboard;
  const [data, setData] = useState(null);
  const [howOpen, setHowOpen] = useState(false);

  useEffect(() => {
    translatorApi.overview().then(setData).catch(() => setData(null));
  }, []);

  const stats = data?.stats || { open: 0, progress: 0, done: 0, dueSoon: 0, overdue: 0 };
  const pairs = data?.profile?.translatorProfile?.langPairs || [];
  const priorityMissions = data?.priorityMissions || [];

  const reload = () => translatorApi.overview().then(setData).catch(() => setData(null));

  const kpis = [
    { icon: Briefcase, label: tr.statOpen, value: stats.open, hint: tr.kpiOpen, cls: 'ah-kpi--teal' },
    { icon: Loader, label: tr.statProgress, value: stats.progress, hint: tr.kpiProgress, cls: 'ah-kpi--navy' },
    { icon: Hourglass, label: tr.statDueSoon, value: stats.dueSoon, hint: tr.kpiDueSoon, cls: `ah-kpi--amber${stats.dueSoon ? ' is-hot' : ''}` },
    { icon: Flame, label: tr.statOverdue, value: stats.overdue, hint: tr.kpiOverdue, cls: `ah-kpi--amber${stats.overdue ? ' is-hot' : ''}` },
  ];

  const shortcuts = [
    { to: '/pro/missions', icon: Briefcase, label: tr.missionsTitle },
    { to: '/pro/availability', icon: UserCog, label: tr.navAvailability || 'Disponibilité' },
    { to: '/pro/profile', icon: UserCheck, label: tr.navProfile },
    { to: '/pro/messages', icon: MessageSquare, label: d.navMessages || 'Messages' },
  ];

  return (
    <div className="ah">
      <header className="ah-hero">
        <div className="ah-hero-text">
          <span className="ah-hero-eyebrow"><Briefcase size={13} />{tr.title}</span>
          <h1>{tr.homeTitle}</h1>
          <p>{tr.homeSubtitle}</p>
        </div>
        <div className="ah-hero-actions">
          <Link to="/pro/availability" className="ah-hero-btn ah-hero-btn--gold">
            <UserCheck size={16} />{tr.navAvailability || 'Disponibilité'}
          </Link>
          <button type="button" className="ah-hero-btn ah-hero-btn--ghost" onClick={reload}>
            <RefreshCw size={16} />{d.refresh || 'Actualiser'}
          </button>
          <button type="button" className="ah-hero-btn ah-hero-btn--ghost" onClick={() => setHowOpen(true)}>
            <HelpCircle size={16} />{d.howItWorks}
          </button>
        </div>
      </header>

      <div className="ah-kpis">
        {kpis.map((k) => (
          <div className={`ah-kpi ${k.cls}`} key={k.label}>
            <span className="ah-kpi-icon"><k.icon size={22} /></span>
            <div className="ah-kpi-body">
              <em>{k.label}</em>
              <strong>{k.value}</strong>
              <small>{k.hint}</small>
            </div>
          </div>
        ))}
      </div>

      <div className="ah-grid">
        <div className="ah-col">
          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><ClipboardList size={16} /></span>
              <h2>{tr.recentMissions}</h2>
              <Link className="dash-link" to="/pro/missions">
                {d.view} <ArrowRight size={13} style={{ verticalAlign: '-2px' }} />
              </Link>
            </div>
            <div className="ah-card-body" style={{ padding: 0 }}>
              <MissionsTable rows={data?.recentMissions || []} />
            </div>
          </section>

          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><Flame size={16} /></span>
              <h2>{tr.priorityMissions}</h2>
              <span className="ah-card-count">{priorityMissions.length}</span>
            </div>
            <div className="ah-card-body">
              {priorityMissions.length ? (
                <div className="ah-queue">
                  {priorityMissions.map((m) => (
                    <div className="ah-queue-item" key={m.id}>
                      <div>
                        <strong>{m.id} · {m.document}</strong>
                        <p>{m.client} · {m.deadline || '—'} · {m.langs}</p>
                      </div>
                      <Link className="dash-link" to={`/pro/missions/${m.id}`}>{d.view}</Link>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="ah-empty">{d.empty}</p>
              )}
            </div>
          </section>
        </div>

        <div className="ah-col">
          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><ArrowRight size={16} /></span>
              <h2>{d.quickActions || 'Actions rapides'}</h2>
            </div>
            <div className="ah-card-body">
              <div className="ah-shortcuts">
                {shortcuts.map((s) => (
                  <Link className="ah-shortcut" to={s.to} key={s.to}>
                    <s.icon size={17} />{s.label}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><Languages size={16} /></span>
              <h2>{d.langPairs}</h2>
            </div>
            <div className="ah-card-body">
              <p style={{ color: 'var(--gray-600)', marginTop: 0, fontSize: '0.85rem' }}>{d.translatorInfoPairs}</p>
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {pairs.length ? pairs.map((p) => (
                  <span key={`${p.source}-${p.target}`} className="dash-badge">{p.source}→{p.target}</span>
                )) : <span className="dash-badge">—</span>}
              </div>
            </div>
          </section>
        </div>
      </div>

      <DashModal open={howOpen} title={d.assignHowTitle} onClose={() => setHowOpen(false)}>
        <p style={{ marginTop: 0, color: 'var(--gray-700)', lineHeight: 1.65 }}>{d.assignHowText}</p>
      </DashModal>
    </div>
  );
}

export function TranslatorMissionsPage() {
  const { t } = useLanguage();
  const tr = t.dashboard.translator;
  const d = t.dashboard;
  const c = d.client;
  const [missions, setMissions] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [sortKey, setSortKey] = useState('recent');
  const [busy, setBusy] = useState(false);
  const [refuseTarget, setRefuseTarget] = useState(null);

  const reload = () => translatorApi.missions().then((res) => setMissions(res.missions || [])).catch(() => setMissions([]));

  useEffect(() => {
    reload();
  }, []);

  const respondTo = async (mission, decision, note = '') => {
    setBusy(true);
    try {
      const res = await translatorApi.respondMission(mission.id, decision, note);
      setMissions((prev) => prev.map((x) => (x.id === mission.id ? { ...x, status: res.mission?.status || decision } : x)));
      setRefuseTarget(null);
    } catch {
      reload();
    } finally {
      setBusy(false);
    }
  };

  const filtered = missions
    .filter((m) => {
      if (statusFilter !== 'all' && m.status !== statusFilter) return false;
      if (serviceFilter !== 'all' && (m.category || 'translation') !== serviceFilter) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return [m.id, m.orderCode, m.document, m.client, m.langs].some((x) => String(x || '').toLowerCase().includes(q));
    })
    .sort((a, b) => {
      if (sortKey === 'deadlineNear') return (a.deadline || '9999').localeCompare(b.deadline || '9999');
      if (sortKey === 'deadlineFar') return (b.deadline || '').localeCompare(a.deadline || '');
      return (b.createdAt || '').localeCompare(a.createdAt || '');
    });
  const missionsPg = usePagination(filtered, 9);

  const hasFilters = search || statusFilter !== 'all' || serviceFilter !== 'all' || sortKey !== 'recent';
  const resetFilters = () => { setSearch(''); setStatusFilter('all'); setServiceFilter('all'); setSortKey('recent'); };

  return (
    <>
      <header className="dash-page-head orders-hero">
        <div className="orders-hero-top">
          <span className="orders-hero-icon"><Briefcase size={22} /></span>
          <div className="orders-hero-text" style={{ flex: 1 }}>
            <h1>{tr.missionsTitle}</h1>
            <p>{tr.missionsSubtitle}</p>
          </div>
        </div>
      </header>

      <div className="orders-filters">
        <div className="orders-filters-head">
          <span className="of-title"><SlidersHorizontal size={13} />{c.filtersTitle}</span>
          <span className="of-meta">
            <span>{filtered.length} / {missions.length}</span>
            {hasFilters && (
              <button type="button" className="of-reset" onClick={resetFilters}>
                <RotateCcw size={13} />{c.resetFilters}
              </button>
            )}
          </span>
        </div>
        <div className="orders-filters-grid">
          <div className="of-field">
            <label>{c.searchLabel}</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tr.searchMissions} />
          </div>
          <div className="of-field">
            <label>{c.colStatus}</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">{tr.filterAll}</option>
              <option value="assigned">{tr.mNew}</option>
              <option value="accepted">{tr.mAccepted}</option>
              <option value="in_progress">{d.status.in_progress}</option>
              <option value="ready">{tr.mToDeliver}</option>
              <option value="delivered">{tr.mDone}</option>
              <option value="refused">{d.status.refused}</option>
              <option value="cancelled">{d.status.cancelled}</option>
            </select>
          </div>
          <div className="of-field">
            <label>{tr.filterService}</label>
            <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
              <option value="all">{tr.filterAll}</option>
              <option value="translation">{c.catTranslation}</option>
              <option value="interpreting">{c.catInterpreting}</option>
            </select>
          </div>
          <div className="of-field">
            <label>{tr.sortLabel}</label>
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
              <option value="recent">{tr.sortRecent}</option>
              <option value="deadlineNear">{tr.sortDeadlineNear}</option>
              <option value="deadlineFar">{tr.sortDeadlineFar}</option>
            </select>
          </div>
        </div>
      </div>

      {missionsPg.pageItems.length === 0 ? (
        <p className="ah-empty">{d.empty}</p>
      ) : (
        <div className="order-cards">
          {missionsPg.pageItems.map((m) => {
            const srcUrl = [
              ...(m.orderDocuments || []),
              ...(m.documents || []).filter((f) => f.type === 'source'),
            ].find((f) => f.url)?.url;
            return (
            <article key={m.id} className="order-card">
              <div className="order-card-head">
                <strong>{m.id}</strong>
                <StatusBadge status={m.status} labels={d.status} />
              </div>
              {m.orderCode && (
                <p className="order-card-line" style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: 2 }}>
                  <Package size={13} /> {tr.colOrder} : {m.orderCode}
                </p>
              )}
              <p className="order-card-line"><Languages size={14} /> {m.langs}</p>
              <p className="order-card-line">
                {(m.category || 'translation') === 'interpreting' ? <Headphones size={14} /> : <FileText size={14} />}
                {(m.category || 'translation') === 'interpreting' ? c.catInterpreting : m.document}
              </p>
              {m.pages ? (
                <p className="order-card-line"><ClipboardList size={14} /> {m.pages} {tr.pagesLabel}</p>
              ) : null}
              <p className="order-card-line"><CalendarDays size={14} /> {tr.colDeadline} : {m.deadline || '—'}</p>
              <p className="order-card-line"><User size={14} /> {tr.colClient} : {m.client || '—'}</p>
              {m.status === 'assigned' && (
                <div style={{ display: 'flex', gap: 8, margin: '8px 0 2px' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ flex: 1, padding: '7px 10px', fontSize: '0.8rem' }}
                    disabled={busy}
                    onClick={() => respondTo(m, 'accepted')}
                  >
                    <Check size={14} /> {tr.acceptMission}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    style={{ flex: 1, padding: '7px 10px', fontSize: '0.8rem' }}
                    disabled={busy}
                    onClick={() => setRefuseTarget(m)}
                  >
                    <X size={14} /> {tr.refuseMission}
                  </button>
                </div>
              )}
              <div className="order-card-foot">
                <span className="order-card-amount">{m.price != null ? `${m.price} ${m.currency}` : ''}</span>
                <span style={{ display: 'inline-flex', gap: 8 }}>
                  {srcUrl && (
                    <a
                      className="order-card-view"
                      style={{ background: 'rgba(26,122,122,0.1)', color: 'var(--teal)' }}
                      href={fileUrl(srcUrl)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Eye size={13} /> {tr.viewDoc}
                    </a>
                  )}
                  <Link className="order-card-view" to={`/pro/missions/${m.id}`}>{tr.viewMission}</Link>
                </span>
              </div>
            </article>
            );
          })}
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        <Pagination page={missionsPg.page} totalPages={missionsPg.totalPages} total={missionsPg.total} onChange={missionsPg.setPage} />
      </div>

      <RefuseMissionModal
        open={Boolean(refuseTarget)}
        busy={busy}
        onClose={() => setRefuseTarget(null)}
        onConfirm={(note) => respondTo(refuseTarget, 'refused', note)}
      />
    </>
  );
}

export function TranslatorMissionDetailPage() {
  const { id } = useParams();
  const { t } = useLanguage();
  const d = t.dashboard;
  const tr = d.translator;
  const [mission, setMission] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyNotes, setVerifyNotes] = useState('');
  const [confirmStatus, setConfirmStatus] = useState(null);
  const [refuseOpen, setRefuseOpen] = useState(false);

  const reload = () => translatorApi.mission(id).then((res) => setMission(res.mission)).catch(() => setMission(null));

  useEffect(() => {
    reload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setStatus = async (status) => {
    setBusy(true);
    setMsg('');
    try {
      const res = await translatorApi.updateMissionStatus(id, status);
      setMission(res.mission);
      setMsg(d.saved);
    } catch {
      setMsg(d.empty);
    } finally {
      setBusy(false);
    }
  };

  const requestVerification = async () => {
    setBusy(true);
    setMsg('');
    try {
      const res = await translatorApi.requestVerification(id, verifyNotes);
      setMission(res.mission);
      setMsg(d.saved);
      setVerifyOpen(false);
      setVerifyNotes('');
    } catch (err) {
      setMsg(err.data?.error || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const onUpload = (kind) => async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setBusy(true);
    try {
      const res = await translatorApi.uploadDocuments(id, files, kind);
      setMission(res.mission);
      setMsg(d.saved);
    } catch {
      setMsg('Upload error');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  if (!mission) return <p className="dash-empty">{d.empty}</p>;

  const orderDocKeys = new Set((mission.orderDocuments || []).map((f) => `${f.name}|${f.url || ''}`));
  const srcDocs = [
    ...(mission.orderDocuments || []),
    ...(mission.documents || []).filter(
      (f) => f.type === 'source' && !orderDocKeys.has(`${f.name}|${f.url || ''}`)
    ),
  ];
  const draftDocs = (mission.documents || []).filter((f) => f.type === 'draft');
  const finalDocs = (mission.documents || []).filter((f) => f.type === 'final');
  const canWork = !['assigned', 'refused', 'cancelled'].includes(mission.status);

  const docGroup = (label, files) => (
    <div style={{ marginBottom: 12 }}>
      <strong style={{ display: 'block', fontSize: '0.82rem', color: 'var(--gray-600)', marginBottom: 6 }}>{label}</strong>
      {files.length === 0 ? (
        <p className="ah-empty" style={{ padding: '6px 0' }}>—</p>
      ) : (
        <ul className="dash-file-list">
          {files.map((f) => (
            <li key={f.id}>
              <span>{f.name}</span>
              {f.url && (
                <span style={{ display: 'inline-flex', gap: 10 }}>
                  <a className="dash-link" href={fileUrl(f.url)} target="_blank" rel="noreferrer"><Eye size={12} style={{ verticalAlign: '-2px' }} /> {d.view}</a>
                  <a className="dash-link" href={fileUrl(f.url)} download><Download size={12} style={{ verticalAlign: '-2px' }} /></a>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const respond = async (decision, note = '') => {
    setBusy(true);
    setMsg('');
    try {
      const res = await translatorApi.respondMission(id, decision, note);
      setMission(res.mission);
      setMsg(d.saved);
    } catch {
      setMsg(d.empty);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Link to="/pro/missions" className="dash-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <ChevronLeft size={15} />{tr.missionsTitle}
      </Link>

      <div className="ah">
        <header className="ah-hero">
          <div className="ah-hero-text">
            <span className="ah-hero-eyebrow">
              <Package size={13} />
              {(mission.category || 'translation') === 'interpreting' ? d.client.catInterpreting : d.client.catTranslation}
            </span>
            <h1>{mission.id}</h1>
            <p>{mission.orderCode ? `${tr.colOrder} ${mission.orderCode} · ` : ''}{mission.document} · {mission.langs}</p>
            <div className="ord-hero-meta">
              <span><CalendarDays size={13} />{mission.createdAt || '—'}</span>
              {mission.deadline ? <span><Hourglass size={13} />{mission.deadline}</span> : null}
              <StatusBadge status={mission.status} labels={d.status} />
            </div>
          </div>
          <div className="ah-hero-actions">
            {mission.price != null && (
              <div className="ord-hero-price">
                <em>{tr.colAmount}</em>
                <strong>{mission.price} {mission.currency || 'TND'}</strong>
              </div>
            )}
            <Link className="ord-contact-btn" to="/pro/messages">
              <MessageSquare size={15} />{tr.contactClient}
            </Link>
          </div>
        </header>

        {msg && <p className="dash-toast">{msg}</p>}

        <div className="ah-masonry">
          {mission.status === 'assigned' && (
            <section className="ah-card">
              <div className="ah-card-head">
                <span className="ah-card-head-icon"><UserCheck size={16} /></span>
                <h3>{tr.respondTitle}</h3>
              </div>
              <div className="ah-card-body">
                <p style={{ marginTop: 0, color: 'var(--gray-600)' }}>{tr.respondHint}</p>
                <div className="dash-actions">
                  <button type="button" className="btn btn-primary" disabled={busy} onClick={() => respond('accepted')}>
                    {tr.acceptMission}
                  </button>
                  <button type="button" className="btn btn-outline" disabled={busy} onClick={() => setRefuseOpen(true)}>
                    {tr.refuseMission}
                  </button>
                </div>
              </div>
            </section>
          )}

          {mission.status !== 'refused' && (
            <section className="ah-card">
              <div className="ah-card-head">
                <span className="ah-card-head-icon"><Loader size={16} /></span>
                <h3>{tr.secActions}</h3>
              </div>
              <div className="ah-card-body">
                <div className="dash-actions">
                  <button type="button" className="btn btn-outline" disabled={busy || mission.status === 'assigned'} onClick={() => setConfirmStatus('in_progress')}>{d.startMission}</button>
                  <button type="button" className="btn btn-outline" disabled={busy || mission.status === 'assigned'} onClick={() => setConfirmStatus('ready')}>{d.markReady}</button>
                  <button type="button" className="btn btn-primary" disabled={busy || mission.status === 'assigned'} onClick={() => setConfirmStatus('delivered')}>{d.markDelivered}</button>
                </div>
                {mission.status === 'ready' && !['verification_requested','lawyer_notary_in_progress','correction_requested','verification_validated','final_control','ready_to_deliver','delivered','completed'].includes(mission.orderStatus) && (
                  <div className="dash-actions" style={{ marginTop: 12 }}>
                    <button type="button" className="btn btn-outline" disabled={busy} onClick={() => setVerifyOpen(true)}>
                      Demander une vérification
                    </button>
                  </div>
                )}
              </div>
            </section>
          )}

          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><Inbox size={16} /></span>
              <h3>{tr.secInfos}</h3>
            </div>
              <div className="ah-card-body">
                <dl className="ord-kv">
                  <dt>{d.missionDetail || 'Mission'}</dt><dd>{mission.id}</dd>
                  <dt>{tr.colOrder}</dt><dd>{mission.orderCode || '—'}</dd>
                  <dt>{tr.filterService}</dt>
                  <dd>{(mission.category || 'translation') === 'interpreting' ? d.client.catInterpreting : d.client.catTranslation}</dd>
                  <dt>{d.client.colLangs}</dt><dd>{mission.langs}</dd>
                  <dt>{tr.colClient}</dt><dd>{mission.client || '—'}</dd>
                  {mission.pages ? (<><dt>Pages</dt><dd>{mission.pages}</dd></>) : null}
                  <dt>{tr.colDate}</dt><dd>{mission.createdAt || '—'}</dd>
                  <dt>{tr.colDeadline}</dt><dd>{mission.deadline || '—'}</dd>
                </dl>
              </div>
            </section>

            <section className="ah-card">
              <div className="ah-card-head">
                <span className="ah-card-head-icon"><FileText size={16} /></span>
                <h3>{tr.secDocs}</h3>
              </div>
              <div className="ah-card-body">
                {docGroup(tr.docsClient, srcDocs)}
                {docGroup(tr.docsWork, draftDocs)}
                {docGroup(tr.docsDeliver, finalDocs)}
                {canWork && (
                  <div style={{ borderTop: '1px solid rgba(12,30,60,0.06)', paddingTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <label className="dash-btn-sm" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Upload size={13} /> {tr.uploadDraft}
                      <input type="file" multiple disabled={busy} onChange={onUpload('draft')} style={{ display: 'none' }} />
                    </label>
                    <label className="btn btn-primary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', fontSize: '0.8rem' }}>
                      <Upload size={13} /> {tr.uploadFinalDoc}
                      <input type="file" multiple disabled={busy} onChange={onUpload('final')} style={{ display: 'none' }} />
                    </label>
                  </div>
                )}
              </div>
            </section>

            <section className="ah-card">
              <div className="ah-card-head">
                <span className="ah-card-head-icon"><ClipboardList size={16} /></span>
                <h3>{tr.secInstructions}</h3>
              </div>
              <div className="ah-card-body">
                <dl className="ord-kv">
                  <dt>{tr.clientNotes}</dt><dd>{mission.orderNotes || '—'}</dd>
                  <dt>{tr.adminNotesLabel}</dt><dd>{mission.adminNotes || mission.notes || '—'}</dd>
                </dl>
              </div>
            </section>

            <section className="ah-card">
              <div className="ah-card-head">
                <span className="ah-card-head-icon"><History size={16} /></span>
                <h3>{tr.secHistory}</h3>
              </div>
              <div className="ah-card-body">
                <ul className="ord-hist">
                  {mission.createdAt && (
                    <li><time>{mission.createdAt}</time><span><strong>{tr.missionAssigned}</strong></span></li>
                  )}
                  {(mission.history || []).map((h, i) => (
                    <li key={i}>
                      <time>{h.at}</time>
                      <span><strong>{h.action}</strong>{h.detail ? ` — ${h.detail}` : ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="ah-card">
              <div className="ah-card-head">
                <span className="ah-card-head-icon"><MessageSquare size={16} /></span>
                <h3>{tr.secComm}</h3>
              </div>
              <div className="ah-card-body" style={{ display: 'grid', gap: 10 }}>
                <Link className="ord-contact-btn" to="/pro/messages">
                  <MessageSquare size={15} />{tr.contactClient}
                </Link>
                <Link className="ord-contact-btn" to="/pro/messages" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a8a 100%)', boxShadow: '0 8px 20px rgba(30,58,95,0.3)' }}>
                  <MessageSquare size={15} />{tr.contactAdmin}
                </Link>
              </div>
            </section>
        </div>
      </div>

      <DashModal
        open={Boolean(confirmStatus)}
        title={tr.confirmTitle}
        onClose={() => setConfirmStatus(null)}
      >
        <p style={{ marginTop: 0, color: 'var(--gray-600)' }}>
          {tr.confirmText}
        </p>
        <p style={{ fontWeight: 700 }}>
          {{ in_progress: d.startMission, ready: d.markReady, delivered: d.markDelivered }[confirmStatus]}
        </p>
        <div className="dash-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={async () => { const s = confirmStatus; setConfirmStatus(null); await setStatus(s); }}
          >
            {tr.confirmBtn}
          </button>
          <button type="button" className="btn btn-outline" onClick={() => setConfirmStatus(null)}>
            {d.cancel}
          </button>
        </div>
      </DashModal>

      <DashModal
        open={verifyOpen}
        title="Demander une vérification"
        onClose={() => setVerifyOpen(false)}
      >
        <div className="dash-form" style={{ maxWidth: '100%' }}>
          <div className="dash-field">
            <label>Motif / précisions pour le vérificateur</label>
            <textarea
              rows={3}
              value={verifyNotes}
              onChange={(e) => setVerifyNotes(e.target.value)}
              placeholder="Points à vérifier, questions juridiques…"
            />
          </div>
          <div className="dash-actions">
            <button type="button" className="btn btn-primary" disabled={busy} onClick={requestVerification}>
              Envoyer la demande
            </button>
            <button type="button" className="btn btn-outline" onClick={() => setVerifyOpen(false)}>
              Annuler
            </button>
          </div>
          {msg && <p className="dash-toast">{msg}</p>}
        </div>
      </DashModal>

      <RefuseMissionModal
        open={refuseOpen}
        busy={busy}
        onClose={() => setRefuseOpen(false)}
        onConfirm={async (note) => { setRefuseOpen(false); await respond('refused', note); }}
      />
    </>
  );
}

export function TranslatorProfilePage() {
  const { t } = useLanguage();
  const d = t.dashboard;
  const tr = d.translator;
  const [user, setUser] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editFrameKey, setEditFrameKey] = useState(0);
  const [pwdOpen, setPwdOpen] = useState(false);

  const reload = () => translatorApi.profile().then((res) => setUser(res.user)).catch(() => setUser(null));

  useEffect(() => {
    reload();
  }, []);

  // Le formulaire embarqué envoie 'translatorUpdated' après une mise à jour réussie :
  // on ferme le modal et on recharge le profil
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data === 'translatorUpdated') {
        setEditOpen(false);
        setEditFrameKey((k) => k + 1);
        reload();
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  return (
    <>
      <header className="dash-page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1>{tr.profileTitle}</h1>
          <p>{tr.profileSubtitle}</p>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-primary" onClick={() => setEditOpen(true)}>
            <User size={15} /> Modifier mon profil
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setPwdOpen(true)}>
            <Lock size={15} /> {t.auth.changePassword || 'Changer le mot de passe'}
          </button>
        </div>
      </header>

      {user ? (
        <TranslatorProfileView user={user} />
      ) : (
        <p className="dash-empty">Chargement du profil…</p>
      )}

      <DashModal
        open={editOpen}
        wide
        fill
        keepMounted
        title="Modifier mon profil"
        subtitle="Même formulaire que la page « Devenir professionnel »"
        onClose={() => { setEditOpen(false); reload(); }}
      >
        <iframe
          key={editFrameKey}
          src="/devenir-traducteur?embed=1&self=1"
          title="Modifier mon profil"
          style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
        />
      </DashModal>

      <DashModal
        open={pwdOpen}
        title={t.auth.changePassword || 'Changer le mot de passe'}
        onClose={() => setPwdOpen(false)}
      >
        <PasswordChangeForm />
      </DashModal>

    </>
  );
}

export function TranslatorAvailabilityPage() {
  const { t } = useLanguage();
  const tr = t.dashboard.translator;
  const d = t.dashboard;
  const [form, setForm] = useState({ availability: 'available', availabilityStatus: 'disponible', acceptsUrgent: true });
  const [msg, setMsg] = useState('');

  useEffect(() => {
    translatorApi.profile().then((res) => {
      const p = res.user?.translatorProfile || {};
      setForm({
        availability: p.availability || 'available',
        availabilityStatus: p.availabilityStatus || 'disponible',
        acceptsUrgent: p.acceptsUrgent !== false,
      });
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      await translatorApi.updateProfile({ translatorProfile: form });
      setMsg(d.saved);
    } catch (err) {
      setMsg(err.data?.error || d.empty);
    }
  };

  return (
    <>
      <header className="dash-page-head"><h1>{tr.availabilityTitle || 'Disponibilité'}</h1><p>{tr.availabilitySubtitle || 'Gérez votre statut et disponibilité.'}</p></header>
      {msg && <p className="dash-toast">{msg}</p>}
      <section className="dash-panel" style={{ maxWidth: 520 }}>
        <form className="dash-form" onSubmit={save}>
          <div className="dash-field">
            <label>Statut</label>
            <select value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })}>
              {['available', 'unavailable', 'busy'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="dash-field">
            <label>Libellé</label>
            <input value={form.availabilityStatus} onChange={(e) => setForm({ ...form, availabilityStatus: e.target.value })} />
          </div>
          <label className="dash-check">
            <input type="checkbox" checked={form.acceptsUrgent} onChange={(e) => setForm({ ...form, acceptsUrgent: e.target.checked })} />
            {tr.acceptsUrgent || 'Accepte les missions urgentes'}
          </label>
          <button type="submit" className="btn btn-primary">{d.save}</button>
        </form>
      </section>
    </>
  );
}

export function TranslatorNotificationsPage() {
  const { t } = useLanguage();
  const tr = t.dashboard.translator;
  const d = t.dashboard;
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const reload = () => translatorApi.notifications().then((res) => setItems(res.notifications || [])).catch(() => setItems([]));
  useEffect(() => {
    reload();
    const timer = setInterval(reload, 10000);
    return () => clearInterval(timer);
  }, []);

  const openNotif = (n) => {
    if (!n.readAt) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)));
      translatorApi.readNotifications({ id: n.id }).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await translatorApi.readNotifications();
      setItems((prev) => prev.map((x) => ({ ...x, readAt: x.readAt || new Date().toISOString() })));
    } catch { /* ignore */ }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{tr.notificationsTitle || 'Notifications'}</h1>
        <p>{tr.notificationsSubtitle || 'Vos alertes.'}</p>
      </header>
      <NotificationsPanel
        items={items}
        onOpen={openNotif}
        onMarkAll={markAll}
        labels={{ all: 'Toutes', unread: 'Non lues', markAllRead: tr.markAllRead || 'Tout marquer comme lu', empty: d.empty }}
      />
    </>
  );
}

export function TranslatorMessagesPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const tr = t.dashboard.translator;
  const d = t.dashboard;
  const [messages, setMessages] = useState([]);
  const [missions, setMissions] = useState([]);
  const [body, setBody] = useState('');
  const [toSel, setToSel] = useState('');
  const [threadKey, setThreadKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const contacts = useMemo(
    () => (missions || []).filter((m) => m.clientId).map((m) => ({ cid: m.clientId, oid: m.orderId, name: m.client || 'Client', sub: m.orderCode || '' })),
    [missions]
  );

  const msgContacts = useMemo(
    () => [
      { key: '', name: tr.administration || 'Administration', sub: 'Tarjemlek' },
      ...contacts.map((x) => ({ key: `${x.cid}|${x.oid}`, name: x.name, sub: x.sub })),
    ],
    [contacts, tr.administration]
  );

  const threads = useMemo(() => {
    const map = new Map();
    messages.forEach((m) => {
      if (!map.has(m.threadKey)) map.set(m.threadKey, { key: m.threadKey, people: new Map(), last: m.createdAt, preview: '' });
      const thread = map.get(m.threadKey);
      [m.from, m.to].forEach((p) => {
        if (p && p.role !== 'translator') thread.people.set(p.id, p);
      });
      if (new Date(m.createdAt) >= new Date(thread.last || 0)) {
        thread.last = m.createdAt;
        thread.preview = m.body;
      }
    });
    return Array.from(map.values())
      .sort((x, y) => new Date(y.last || 0) - new Date(x.last || 0))
      .map((thread) => ({
        ...thread,
        name: Array.from(thread.people.values()).map((p) => (p.role === 'admin' ? (tr.administration || 'Administration') : p.name)).filter(Boolean).join(' ↔ ') || thread.key,
      }));
  }, [messages, tr.administration]);

  const reload = () => translatorApi.messages().then((res) => setMessages(res.messages || [])).catch(() => setMessages([]));
  useEffect(() => {
    reload();
    translatorApi.missions().then((res) => setMissions(res.missions || [])).catch(() => setMissions([]));
    const timer = setInterval(reload, 4000);
    return () => clearInterval(timer);
  }, []);

  const pickThread = (key) => {
    setThreadKey(key);
    if (key?.startsWith('order:')) {
      const contact = contacts.find((x) => `order:${x.oid}` === key);
      setToSel(contact ? `${contact.cid}|${contact.oid}` : '');
    } else {
      setToSel('');
    }
  };

  const filtered = threadKey && threadKey !== 'new' ? messages.filter((m) => m.threadKey === threadKey) : [];

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) {
      setMsg(t.auth?.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const [cid, oid] = toSel ? toSel.split('|') : [null, null];
      await translatorApi.sendMessage({ body, ...(cid ? { toId: cid, orderId: oid, threadKey: `order:${oid}` } : {}) });
      const key = cid ? `order:${oid}` : threadKey && threadKey !== 'new' ? threadKey : `pro:${user?.id}`;
      setBody('');
      reload();
      setThreadKey(key);
    } catch (err) {
      setMsg(err.data?.error || d.empty);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="dash-page-head"><h1>{tr.messagesTitle || 'Messages'}</h1><p>{tr.messagesSubtitle || 'Échangez avec l\'administrateur.'}</p></header>
      {msg && <p className="dash-toast">{msg}</p>}
      <MessengerPanel
        threads={threads}
        activeKey={threadKey}
        onSelect={pickThread}
        messages={filtered}
        myRole="translator"
        body={body}
        setBody={setBody}
        onSend={send}
        busy={busy}
        labels={{
          conversations: 'Conversations',
          newMessage: 'Nouveau message',
          selectConversation: 'Sélectionnez une conversation',
          writeMessage: 'Écrire un message…',
          empty: d.empty,
          send: tr.sendMessage || 'Envoyer',
        }}
        contacts={msgContacts}
        contactKey={toSel}
        onPickContact={setToSel}
        contactsTitle={tr.recipient || 'Destinataire'}
      />
    </>
  );
}
